import * as i0 from '@angular/core';
import { EventEmitter, Component, ViewChild, Input, Output, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@angular/forms';
import { NgModel, FormsModule } from '@angular/forms';
import { ValueAccessorBase, CapturumSharedModule } from '@capturum/ui/api';
import * as i3 from 'ngx-quill';
import { QuillEditorComponent, QUILL_CONFIG_TOKEN, QuillModule } from 'ngx-quill';
import QuillImageDropAndPaste from 'quill-image-drop-and-paste';
function EditorComponent_quill_editor_0_Template(rf, ctx) {
  if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "quill-editor", 2);
    i0.ɵɵlistener("ngModelChange", function EditorComponent_quill_editor_0_Template_quill_editor_ngModelChange_0_listener($event) {
      i0.ɵɵrestoreView(_r3);
      const ctx_r2 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r2.value = $event);
    })("onContentChanged", function EditorComponent_quill_editor_0_Template_quill_editor_onContentChanged_0_listener($event) {
      i0.ɵɵrestoreView(_r3);
      const ctx_r4 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r4.onChange.emit($event));
    })("onFocus", function EditorComponent_quill_editor_0_Template_quill_editor_onFocus_0_listener($event) {
      i0.ɵɵrestoreView(_r3);
      const ctx_r5 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r5.onFocus.emit($event));
    })("onBlur", function EditorComponent_quill_editor_0_Template_quill_editor_onBlur_0_listener($event) {
      i0.ɵɵrestoreView(_r3);
      const ctx_r6 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r6.onBlur.emit($event));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵclassMap("cap-editor " + ctx_r0.styleClass);
    i0.ɵɵclassProp("use-quill-view-enabled", ctx_r0.useQuillHtmlView && !ctx_r0.disabled);
    i0.ɵɵproperty("ngModel", ctx_r0.value)("id", ctx_r0.inputId)("modules", ctx_r0.config == null ? null : ctx_r0.config.modules)("formats", ctx_r0.config == null ? null : ctx_r0.config.formats)("debounceTime", ctx_r0.debounceTime || 500)("readOnly", ctx_r0.disabled || (ctx_r0.config == null ? null : ctx_r0.config.readOnly) || false)("placeholder", (ctx_r0.config == null ? null : ctx_r0.config.placeholder) || "");
  }
}
function EditorComponent_quill_view_html_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "quill-view-html", 3);
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("content", ctx_r1.value);
  }
}
const defaultModulesConfig = {
  toolbar: {
    container: [['bold', 'italic', 'underline'], [{
      list: 'ordered'
    }, {
      list: 'bullet'
    }], [{
      indent: '-1'
    }, {
      indent: '+1'
    }], [{
      size: ['small', false, 'large', 'huge']
    }], [{
      align: ''
    }, {
      align: 'center'
    }, {
      align: 'right'
    }, {
      align: 'justify'
    }], [{
      header: [1, 2, 3, 4, 5, 6, false]
    }], ['link', 'image']],
    handlers: {
      image: imageUrlHandler
    }
  }
};
// Handler for image, enter image url instead of upload.
function imageUrlHandler() {
  const tooltip = this.quill.theme.tooltip;
  const originalSave = tooltip.save;
  const originalHide = tooltip.hide;
  tooltip.save = function () {
    const range = this.quill.getSelection(true);
    const value = this.textbox.value;
    if (value) {
      this.quill.insertEmbed(range.index, 'image', value, 'user');
    }
  };
  // Called on hide and save.
  tooltip.hide = function () {
    tooltip.save = originalSave;
    tooltip.hide = originalHide;
    tooltip.hide();
  };
  tooltip.edit('image');
  tooltip.textbox.placeholder = 'Image URL';
}
let EditorComponent = /*#__PURE__*/(() => {
  class EditorComponent extends ValueAccessorBase {
    constructor() {
      super(...arguments);
      this.allowImageDropOrPaste = true;
      this.onChange = new EventEmitter();
      this.onFocus = new EventEmitter();
      this.onBlur = new EventEmitter();
      this._config = {
        placeholder: '',
        readOnly: false,
        modules: {
          ...defaultModulesConfig,
          imageDropAndPaste: {
            handler: this.imageDropOrPasteHandler.bind(this)
          }
        }
      };
    }
    set config(value) {
      this._config = value;
      // Ensure that allowImageDropOrPaste functionality works
      this._config.modules = {
        ...this._config.modules,
        imageDropAndPaste: {
          handler: this.imageDropOrPasteHandler.bind(this)
        }
      };
    }
    get config() {
      return this._config;
    }
    imageDropOrPasteHandler(dataUrl, type, imageData) {
      if (this.allowImageDropOrPaste) {
        const caretPosition = this.quill?.quillEditor?.getSelection(true);
        this.quill?.quillEditor?.insertEmbed(caretPosition?.index || 0, 'image', dataUrl);
      }
    }
    setDisabledState(isDisabled) {
      this.disabled = isDisabled;
    }
    static {
      this.ɵfac = /* @__PURE__ */function () {
        let ɵEditorComponent_BaseFactory;
        return function EditorComponent_Factory(t) {
          return (ɵEditorComponent_BaseFactory || (ɵEditorComponent_BaseFactory = i0.ɵɵgetInheritedFactory(EditorComponent)))(t || EditorComponent);
        };
      }();
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: EditorComponent,
        selectors: [["cap-editor"]],
        viewQuery: function EditorComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(QuillEditorComponent, 5);
            i0.ɵɵviewQuery(NgModel, 7);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.quill = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.model = _t.first);
          }
        },
        inputs: {
          styleClass: "styleClass",
          allowImageDropOrPaste: "allowImageDropOrPaste",
          disabled: "disabled",
          debounceTime: "debounceTime",
          inputId: "inputId",
          useQuillHtmlView: "useQuillHtmlView",
          config: "config"
        },
        outputs: {
          onChange: "onChange",
          onFocus: "onFocus",
          onBlur: "onBlur"
        },
        features: [i0.ɵɵProvidersFeature([ValueAccessorBase.getProviderConfig(EditorComponent)]), i0.ɵɵInheritDefinitionFeature],
        decls: 2,
        vars: 2,
        consts: [[3, "ngModel", "class", "use-quill-view-enabled", "id", "modules", "formats", "debounceTime", "readOnly", "placeholder", "ngModelChange", "onContentChanged", "onFocus", "onBlur", 4, "ngIf"], ["class", "use-quill-view", "theme", "snow", 3, "content", 4, "ngIf"], [3, "ngModel", "id", "modules", "formats", "debounceTime", "readOnly", "placeholder", "ngModelChange", "onContentChanged", "onFocus", "onBlur"], ["theme", "snow", 1, "use-quill-view", 3, "content"]],
        template: function EditorComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵtemplate(0, EditorComponent_quill_editor_0_Template, 1, 11, "quill-editor", 0);
            i0.ɵɵtemplate(1, EditorComponent_quill_view_html_1_Template, 1, 1, "quill-view-html", 1);
          }
          if (rf & 2) {
            i0.ɵɵproperty("ngIf", ctx.useQuillHtmlView && !ctx.disabled || !ctx.useQuillHtmlView);
            i0.ɵɵadvance(1);
            i0.ɵɵproperty("ngIf", ctx.useQuillHtmlView && ctx.disabled);
          }
        },
        dependencies: [i1.NgIf, i2.NgControlStatus, i2.NgModel, i3.QuillEditorComponent, i3.QuillViewHTMLComponent]
      });
    }
  }
  return EditorComponent;
})();
(function () {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let CapturumEditorModule = /*#__PURE__*/(() => {
  class CapturumEditorModule {
    static forRoot(config) {
      return {
        ngModule: CapturumEditorModule,
        providers: [{
          provide: QUILL_CONFIG_TOKEN,
          useValue: {
            customModules: [...(Array.isArray(config?.customModules) ? config.customModules : []), {
              path: 'modules/imageDropAndPaste',
              implementation: QuillImageDropAndPaste
            }]
          }
        }]
      };
    }
    static {
      this.ɵfac = function CapturumEditorModule_Factory(t) {
        return new (t || CapturumEditorModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: CapturumEditorModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [CommonModule, FormsModule, QuillModule.forRoot({
          customModules: [{
            path: 'modules/imageDropAndPaste',
            implementation: QuillImageDropAndPaste
          }]
        }), CapturumSharedModule]
      });
    }
  }
  return CapturumEditorModule;
})();
(function () {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { CapturumEditorModule, EditorComponent, defaultModulesConfig };
